/**
 * Image Optimization Utilities
 * 
 * Uses wsrv.nl (weserv.nl) - a free image CDN proxy that resizes and converts images
 * This significantly reduces bandwidth for large hotel images
 */

/**
 * Optimize an image URL using wsrv.nl proxy
 * @param {string} url - Original image URL
 * @param {object} options - Optimization options
 * @param {number} options.width - Target width
 * @param {number} options.height - Target height (optional)
 * @param {string} options.output - Output format (webp, jpg, png) - default webp
 * @param {number} options.quality - Quality 1-100, default 80
 * @returns {string} Optimized image URL
 */
export function optimizeImageUrl(url, options = {}) {
  if (!url) return url;
  
  // Skip if already optimized or is a data URL
  if (url.includes('wsrv.nl') || url.startsWith('data:')) {
    return url;
  }
  
  // Skip local images (they're already optimized)
  if (url.startsWith('/') || url.startsWith('./')) {
    return url;
  }
  
  const {
    width = 400,
    height,
    output = 'webp',
    quality = 80,
  } = options;
  
  // Build wsrv.nl URL
  // Documentation: https://images.weserv.nl/docs/
  const params = new URLSearchParams();
  params.set('url', url);
  params.set('w', width.toString());
  if (height) {
    params.set('h', height.toString());
  }
  params.set('output', output);
  params.set('q', quality.toString());
  params.set('fit', 'cover'); // Cover mode for aspect ratio preservation
  params.set('we', '1'); // Enable WebP auto-detection
  
  return `https://wsrv.nl/?${params.toString()}`;
}

/**
 * Optimize hotel thumbnail for card display (220x147)
 * Uses 1.5x for retina displays (balance between quality and file size)
 */
export function optimizeHotelThumbnail(url) {
  return optimizeImageUrl(url, {
    width: 330,  // 220 * 1.5 for retina
    height: 220, // 147 * 1.5 for retina
    quality: 75,
  });
}

/**
 * Optimize deal card image (313x209)
 * Uses 1.5x for retina displays (balance between quality and file size)
 */
export function optimizeDealImage(url) {
  return optimizeImageUrl(url, {
    width: 470,  // 313 * 1.5 for retina
    height: 314, // 209 * 1.5 for retina
    quality: 75,
  });
}

/**
 * Optimize destination image (238x179)
 */
export function optimizeDestinationImage(url) {
  return optimizeImageUrl(url, {
    width: 357,  // 238 * 1.5 for retina
    height: 269, // 179 * 1.5 for retina
    quality: 75,
  });
}

export default {
  optimizeImageUrl,
  optimizeHotelThumbnail,
  optimizeDealImage,
  optimizeDestinationImage,
};
